import React, { useEffect, useState, useContext } from "react";
import { useDeliverySession } from "../../hooks/useDeliverySession";
import CommonTitle from "../common/DeliveryCommonTitle";
import { fetchBuildingsByPincode } from "../../services/delivery_api";
import { generateUniqueFileCode } from "../../utils/generateUniqueFileCode";
import { useTranslation } from "react-i18next"; // ← ADD THIS
import { AuthContext } from "../../context/AuthContext"; // adjust path if needed
const DeliveryLocationScreen = ({ onBack, onNext }) => {
  const { t } = useTranslation(); // ← ADD THIS
  const { session, updateDeliveryField, resetSessionForNextUse } =
    useDeliverySession();
  const { user } = useContext(AuthContext);
  const [pin_code, setPinCode] = useState(session.pin_code || "");
  const [pinCodeError, setPinCodeError] = useState(false);
  const [buildingNameError, setBuildingNameError] = useState(false);
  const [noOfParcel, setNoOfParcel] = useState(
    session.total_no_of_delivery || "",
  );
  const [noOfParcelError, setNoOfParcelError] = useState(false);
  const [buildingName, setBuildingName] = useState("");
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    console.log("Current User:", user);
  }, [user]);

  useEffect(() => {
    resetSessionForNextUse();
    setPinCode("");

    localStorage.removeItem("shipping_parcel_count");
    // Force clear the parcel input
    setNoOfParcel("");
  }, []);

  useEffect(() => {
    if (selectedBuilding) {
      console.log("🏢 Selected Building:", selectedBuilding);
    }
  }, [selectedBuilding]);

  // Debounced fetch buildings on pin code change
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (pin_code.length === 6) {
        fetchBuildings(pin_code);
      } else {
        setBuildingOptions([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [pin_code]);

  const fetchBuildings = async (pin_code) => {
    try {
      const result = await fetchBuildingsByPincode(pin_code);
      if (result?.success && Array.isArray(result?.data)) {
        setBuildingOptions(result.data);
      } else {
        setBuildingOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch buildings:", error);
      setBuildingOptions([]);
    }
  };

  const handleProceed = () => {
    const isPinEmpty = !pin_code.trim();
    const isBuildingEmpty = !buildingName.trim();
    const isParcelEmpty = !noOfParcel;

    setPinCodeError(isPinEmpty);
    setBuildingNameError(isBuildingEmpty);
    setNoOfParcelError(isParcelEmpty);

    if (isPinEmpty || isBuildingEmpty || isParcelEmpty || !selectedBuilding) {
      return;
    }

    const fileCode = generateUniqueFileCode();
    console.log("📦 Generated File Code:", fileCode);
    updateDeliveryField("file_ident_code", fileCode);

    const buildingId = selectedBuilding?.id;

    updateDeliveryField("total_no_of_delivery", noOfParcel);
    updateDeliveryField("building", buildingId);
    updateDeliveryField("pin_code", pin_code);
    updateDeliveryField("building_name", buildingName);

    const payload = {
      pin_code,
      building_name: buildingName,
      total_no_of_delivery: noOfParcel,
      building_id: buildingId,
    };

    console.log("📦 Delivery Location Payload:", payload);
    setTimeout(() => {
      console.log("✅ Final Session Code:", session?.file_ident_code);
      onNext(payload);
    }, 200);
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <div className="w-full min-h-screen px-4 sm:px-6 py-6 relative">
      <CommonTitle
        title={t("tmwDeliveryLocations")}
        showTitle={true}
        showBack={true}
        onBack={handleBack}
      />

      <div className="flex items-center justify-center pt-16 px-4">
        <div className="bg-white rounded-xl shadow-md p-8 w-full max-w-md space-y-6">
          {/* Pin Code Input */}
          <div>
            <input
              placeholder={t("pinCode")}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin_code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ""); // Only digits
                if (value.length <= 6) {
                  setPinCode(value);
                  setPinCodeError(false);
                }
              }}
              className={`w-full border-2 rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                pinCodeError ? "border-red-500" : "border-gray-300"
              }`}
            />
            {pinCodeError && (
              <p className="text-red-500 text-sm mt-1">
                {t("pinCode")} {t("isRequired", "is required")}
              </p>
            )}
          </div>

          {/* Building Dropdown */}
          <div className="relative">
            <select
              value={buildingName}
              onChange={(e) => {
                const selected = buildingOptions.find(
                  (b) => b.building_name === e.target.value,
                );
                setBuildingName(e.target.value);
                setSelectedBuilding(selected || null);
                setBuildingNameError(false);
              }}
              className={`w-full border-2 rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none ${
                buildingNameError ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="" disabled>
                {t("buildingName", "Select Building")} *
              </option>
              {buildingOptions.map((building) => (
                <option key={building.id} value={building.building_name}>
                  {building.building_name}
                </option>
              ))}
            </select>

            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg
                className="h-5 w-5 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {buildingNameError && (
              <p className="text-red-500 text-sm mt-1">
                {t("buildingName")} {t("isRequired", "is required")}
              </p>
            )}
          </div>

          {/* Number of Parcels */}
          <div>
            <input
              type="text"
              inputMode="numeric"
              placeholder={t("totalNumberOfDeliveries")}
              value={noOfParcel}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value === "") {
                  setNoOfParcel("");
                  setNoOfParcelError(false);
                  return;
                }
                const num = parseInt(value, 10);
                if (!isNaN(num) && num <= 99) {
                  setNoOfParcel(num);
                  setNoOfParcelError(false);
                }
              }}
              className={`w-full border-2 rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                noOfParcelError ? "border-red-500" : "border-gray-300"
              }`}
            />
            {noOfParcelError && (
              <p className="text-red-500 text-sm mt-1">
                {t("totalNumberOfDeliveries")} {t("isRequired", "is required")}
              </p>
            )}
          </div>

          {/* Proceed Button */}
          <button
            onClick={handleProceed}
            disabled={
              !pin_code || !buildingName || !noOfParcel || !selectedBuilding
            }
            className={`w-full text-white font-bold py-4 px-4 rounded-lg text-xl transition duration-200 shadow-md ${
              !pin_code || !buildingName || !noOfParcel || !selectedBuilding
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {t("proceed")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryLocationScreen;
